import { useCallback, useState } from "react";

import { ResourcePermissionConstants } from "../../../../../constants";
import { GetTaskBoardResponse } from "../../../../../service/query";
import { useAuth } from "../../../../../useAuth";
import { NewTaskParentObject } from "../models";
import { useNewTaskParentObjects } from "./useNewTaskParentObjects";

/** Which step of the New-task flow is on screen. */
type NewTaskStep = "closed" | "type" | "object";

export interface UseTasksDashboardNewTaskReturnData {
  /** Wired to the board header's "New task" button — opens the type step. */
  onNewTask: () => void;

  showNewTaskTypeModal: boolean;
  canCreateWorkflowTask: boolean;
  canCreateGeneralTask: boolean;
  /** True while the object sources load — the type step's Confirm waits on them. */
  isLoadingNewTaskTypes: boolean;
  /** Type chosen → move on to the object step. */
  handleNewTaskType: (isGeneralTask: boolean) => void;

  showNewTaskObjectModal: boolean;
  /** The current page of matches the chosen type can hang off. */
  newTaskObjects: NewTaskParentObject[];
  isLoadingNewTaskObjects: boolean;
  newTaskObjectsHaveError: boolean;
  /** Searches the objects server-side for the typed term. */
  handleNewTaskObjectSearch: (term: string) => void;
  /** Object chosen → the overlay opens in create mode against it. */
  handleNewTaskObject: (object: NewTaskParentObject) => void;

  handleCancelNewTask: () => void;

  /** Create-mode state for the shared `WorkflowTaskPanel`. */
  createTaskIsGeneral: boolean;
  createTaskParent: NewTaskParentObject | undefined;
  createTaskWorkflow: GetTaskBoardResponse["results"][number] | undefined;
  showCreateTaskPanel: boolean;
  /** Clears the create-mode state when the overlay closes. */
  handleCloseCreateTaskPanel: () => void;
}

/**
 * The "New task" flow on the Tasks dashboard (KAN-9565, REQ-014): type, then parent object, then the
 * existing overlay in create mode.
 *
 * Two steps rather than the SDD's one because the dashboard is org-wide — the per-object task tabs get
 * the parent from their route, and here there is none to inherit. Type comes first so each step's
 * options can be sourced for the other: the object list is the set that type can be created against,
 * which is what stops the user picking an object the API would then reject.
 *
 * The type step reuses the `NewTaskModal` the project and landholding task tabs already use, so the
 * board header's own button stays a plain button and the widget's public API is untouched.
 */
export const useTasksDashboardNewTask = (): UseTasksDashboardNewTaskReturnData => {
  const { hasPermission } = useAuth();

  const [step, setStep] = useState<NewTaskStep>("closed");
  const [createTaskIsGeneral, setCreateTaskIsGeneral] = useState(false);
  const [createTaskParent, setCreateTaskParent] = useState<NewTaskParentObject>();
  const [showCreateTaskPanel, setShowCreateTaskPanel] = useState(false);

  /*
    The option queries are only worth issuing once the user is actually creating something, and which
    sources they hit follows the chosen type. The type step runs with `isGeneralTask` still false, so its
    first load is the workflow branch's — which is also what answers whether that type is available.
  */
  const { objects, hasWorkflowObjects, isLoading, hasError, searchObjects } = useNewTaskParentObjects({
    enabled: step !== "closed",
    isGeneralTask: createTaskIsGeneral,
  });

  /*
    The create gates the API applies, as far as they can be known before an object is chosen:

    - general (GeneralTaskCreationStrategy) needs createResource on the acting org — the parent's own
      read permission can't be checked here, so an object the user can't read fails at save rather than
      being withheld from the list;
    - workflow-step (WorkflowStepTaskCreationStrategy) needs Contributor+ on the workflow, which is per
      object — so the type is offered when *any* object qualifies, and the objects offered for that type
      are already reduced to those (see `useNewTaskParentObjects`).
  */
  const canCreateGeneralTask = hasPermission(ResourcePermissionConstants.CREATE_RESOURCE);
  /*
    Offered while the workflow objects are still loading so the option isn't missing from a modal the
    user is already reading; the type step's Confirm is disabled until they land, so the choice can only
    be made once the gate is known.
  */
  const canCreateWorkflowTask = isLoading || hasWorkflowObjects;

  const onNewTask = useCallback((): void => {
    setCreateTaskIsGeneral(false);
    setCreateTaskParent(undefined);
    setShowCreateTaskPanel(false);
    setStep("type");
  }, []);

  const handleNewTaskType = useCallback((isGeneralTask: boolean): void => {
    setCreateTaskIsGeneral(isGeneralTask);
    setStep("object");
  }, []);

  const handleNewTaskObject = useCallback((object: NewTaskParentObject): void => {
    setCreateTaskParent(object);
    setStep("closed");
    setShowCreateTaskPanel(true);
  }, []);

  const handleCancelNewTask = useCallback((): void => {
    setStep("closed");
    setCreateTaskIsGeneral(false);
    setCreateTaskParent(undefined);
  }, []);

  const handleCloseCreateTaskPanel = useCallback((): void => {
    setShowCreateTaskPanel(false);
    setCreateTaskIsGeneral(false);
    setCreateTaskParent(undefined);
  }, []);

  return {
    onNewTask,

    showNewTaskTypeModal: step === "type",
    canCreateWorkflowTask,
    canCreateGeneralTask,
    isLoadingNewTaskTypes: isLoading,
    handleNewTaskType,

    showNewTaskObjectModal: step === "object",
    newTaskObjects: objects,
    isLoadingNewTaskObjects: isLoading,
    newTaskObjectsHaveError: hasError,
    handleNewTaskObjectSearch: searchObjects,
    handleNewTaskObject,

    handleCancelNewTask,

    createTaskIsGeneral,
    createTaskParent,
    // A general task hangs off its parent object directly, so it is given no workflow to derive steps
    // from even when the object it was created against happens to have one.
    createTaskWorkflow: createTaskIsGeneral ? undefined : createTaskParent?.workflow,
    showCreateTaskPanel,
    handleCloseCreateTaskPanel,
  };
};
